// Types
import { StreamUpdateInput, UserRole } from 'api/graphql-global-types';
import { GetStreamParticipants_getStreamParticipants_entities } from 'api/streams/types/GetStreamParticipants';

/**
 * The minimum a stream must carry to rewrite its participant list:
 * `updateStream` validates these fields even when they are unchanged, so the
 * current values are resent untouched.
 */
export type ParticipantsUpdateStream = {
  id: string;
  name: string | null;
  description: string;
  scheduleDate: string | null;
  sponsors:
    | {
        id: string;
        name: string;
        logo?: string | null;
        logoUrl?: string | null;
      }[]
    | null;
  timeZone: { tzCode: string | null; offset?: number | null } | null;
};

export const buildParticipantsUpdateInput = (
  stream: ParticipantsUpdateStream,
  participantIds: string[]
): StreamUpdateInput => ({
  id: stream.id,
  participantIds,
  description: stream.description,
  name: stream.name ?? '',
  scheduleDate: stream.scheduleDate,
  sponsors: stream.sponsors
    ? stream.sponsors.map((sponsor) => ({
        id: sponsor.id,
        name: sponsor.name,
        logo: sponsor.logo ?? sponsor.logoUrl ?? null,
      }))
    : null,
  timeZone: {
    tzCode: stream.timeZone?.tzCode,
    offset: stream.timeZone?.offset,
  },
});

type ParticipantProfile = {
  id: string;
  firstName: string;
  lastName: string;
  slug: string | null;
  storeDetails: {
    id?: string;
    storeName: string | null;
    avatarURL: string | null;
  } | null;
};

/** Lists someone who is not on a stream's roster, such as an interview guest or a fight-card competitor. */
export const toStreamParticipant = (
  profile: ParticipantProfile
): GetStreamParticipants_getStreamParticipants_entities => ({
  __typename: 'User',
  id: profile.id,
  firstName: profile.firstName,
  lastName: profile.lastName,
  slug: profile.slug,
  avatarUrl: profile.storeDetails?.avatarURL ?? null,
  role: UserRole.Athlete,
  storeDetails: profile.storeDetails
    ? {
        __typename: 'StoreDetails',
        id: profile.storeDetails.id ?? profile.id,
        storeName: profile.storeDetails.storeName,
        avatarURL: profile.storeDetails.avatarURL,
      }
    : null,
});
